import { PostStatus } from "@/constants";
import { useDisclosure } from "@/hooks";
import { useCreatePost } from "@/hooks/query-hooks/usePosts";
import { useNotificationStore } from "@/states/zustand/notification";
import { Button, Form, Input, Modal, Space } from "antd";
import { UploadChangeParam, UploadFile } from "antd/es/upload";
import compressImage, { Options } from "browser-image-compression";
import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { AddedImages, AddedImagesPreview, ImageUploadFormItem, PostImage } from "./components";

export function CreateNewPost() {

    const [opened, { toggle }] = useDisclosure(false);
    const [images, setImages] = useState<PostImage[]>([]);
    const [isEditingImages, setIsEditingImages] = useState(false);
    const [isCompressing, setIsCompressing] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const [form] = Form.useForm();

    const { mutate } = useCreatePost();

    const onFinish = async (values: {
        uploadedImages: { file: UploadFile, fileList: UploadFile[] };
        title: string;
        body: string;
    }, status: PostStatus) => {

        const { uploadedImages, title, body } = values;

        // Validate if title, body and at least one image is provided when 
        if (!(title && body && uploadedImages?.fileList.length > 0)) {
            form.validateFields();
            useNotificationStore.getState().setNotification({
                type: "warning",
                message: "Please fill in all required fields & upload at least one image.",
            })
            return;
        }
        if (status === PostStatus.Published) {
            setIsCreating(true);
        } else {
            setIsSavingDraft(true);
        }

        let compressedImages: File[] = [];
        const compressOptions: Options = {
            maxSizeMB: 3,
            useWebWorker: true,
        }

        if (uploadedImages?.fileList.length > 0) {
            const fileList = uploadedImages.fileList ?? [];

            if (fileList && fileList.length > 0) {
                setIsCompressing(true);
                try {
                    compressedImages = await Promise.all(fileList.map(async (file) => {
                        const compressedFile = await compressImage(file.originFileObj as File, compressOptions);
                        return new File([compressedFile], file.name, { type: file.type });
                    }))
                } catch (error) {
                    console.error("Error compressing images:", error);
                } finally {
                    setIsCompressing(false);
                }
            }
        }

        const formData = new FormData();
        const data = new Blob([JSON.stringify({ body, title })], { type: "application/json" })
        formData.append("data", data);
        compressedImages.forEach((image) => {
            formData.append("images", image);
        });

        mutate({ status, data: formData }, {
            onSuccess: () => {
                onToggleModal();
                if (status === PostStatus.Draft) {
                    setIsSavingDraft(false);
                }
            },
            onError: (error: string) => {
                console.error("Error creating post:", error);
                setIsCreating(false);
                setIsSavingDraft(false);
            }
        })
    };

    const onImageChange = async (info: UploadChangeParam<UploadFile>) => {
        const { fileList } = info;
        const uploadedImages = fileList.map((file) => {
            return {
                imgUrl: URL.createObjectURL(file.originFileObj as File),
                file: file,
            }
        });
        setImages(uploadedImages);
    };

    const onRemoveImage = (image: PostImage) => {
        const filteredImages = images.filter(img => img.imgUrl !== image.imgUrl);
        setImages(filteredImages);
        form.setFieldsValue({
            uploadedImages: {
                fileList: filteredImages.map(img => img.file)
            }
        });
        if (images.length === 1) {
            setIsEditingImages(false);
        }
    };

    const onToggleModal = () => {
        toggle();
        form.resetFields();
        setImages([]);
        setIsEditingImages(false);
        setIsCompressing(false);
        setIsCreating(false);
        setIsSavingDraft(false);
    }

    return (
        <>
            <Button
                icon={<AiOutlinePlus />}
                onClick={onToggleModal}
                type="primary"
                shape="circle"
                size="large"
                className="bg-primary-gradient"
            />
            <Form
                form={form}
                onFinish={() => onFinish(form.getFieldsValue(), PostStatus.Published)}
                name="create-new-post-form"
                className="hidden"
            >
                <Modal
                    open={opened}
                    onCancel={onToggleModal}
                    title={
                        isEditingImages ? "Images" : "Create New Post"
                    }
                    centered
                    width={{
                        xs: "90%",
                        sm: "90%",
                        md: isEditingImages ? "100%" : "70%",
                        lg: isEditingImages ? "75%" : "50%",
                        xl: isEditingImages ? "65%" : "35%",
                    }}
                    okText="Post Now"
                    cancelText="Save Draft"
                    destroyOnClose
                    classNames={{
                        header: "[&>.ant-modal-title]:!text-xl [&>.ant-modal-title]:!font-poppins",
                        body: "max-h-[500px] min-h-[300px] overflow-y-auto !py-4",
                    }}
                    footer={(_, { OkBtn }) => (
                        <>
                            {
                                isEditingImages ? (
                                    <Space>
                                        <ImageUploadFormItem
                                            images={images}
                                            onImageChange={onImageChange}
                                            variant="edit-add-more"
                                        />
                                        <Button
                                            className="!font-poppins"
                                            size="large"
                                            shape="round"
                                            onClick={() => setIsEditingImages(false)}
                                            type="primary"
                                        >
                                            Done
                                        </Button>
                                    </Space>
                                ) : (
                                    <div
                                        className="flex items-center justify-between gap-2"
                                    >
                                        {
                                            images.length === 0 && (
                                                <ImageUploadFormItem
                                                    images={images}
                                                    onImageChange={onImageChange}
                                                    variant="default"
                                                />
                                            )
                                        }
                                        <Space
                                            className="ml-auto"
                                        >
                                            <Button
                                                size="large"
                                                shape="round"
                                                disabled={isCompressing || isCreating}
                                                loading={isSavingDraft}
                                                onClick={() => onFinish(form.getFieldsValue(), PostStatus.Draft)}
                                            >
                                                Save Draft
                                            </Button>
                                            <OkBtn />
                                        </Space>
                                    </div>
                                )
                            }
                        </>
                    )}
                    okButtonProps={{
                        type: "primary",
                        htmlType: "submit",
                        form: "create-new-post-form",
                        className: "bg-primary-gradient",
                        size: "large",
                        shape: "round",
                        loading: isCreating,
                        disabled: isSavingDraft || isCompressing,
                    }}
                    maskClosable={!isCreating && !isCompressing}
                >
                    {
                        isEditingImages ? (
                            <AddedImagesPreview
                                images={images}
                                onRemoveImage={onRemoveImage}
                            />
                        ) : (
                            <>
                                <Form.Item
                                    name={"title"}
                                    rules={[{ required: true, message: "Please enter post title" }]}
                                    className="!mb-0"
                                >
                                    <Input.TextArea
                                        placeholder="Title . . ."
                                        autoSize={{ minRows: 1, maxRows: 3 }}
                                        className="!text-xl font-semibold !p-0"
                                        variant="borderless"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={"body"}
                                    rules={[{ required: true, message: "Please enter post content" }]}
                                    className="!mb-0"
                                >
                                    <Input.TextArea
                                        placeholder="Content. . ."
                                        autoSize={{ minRows: 3, maxRows: 5 }}
                                        className="!text-base !p-0"
                                        variant="borderless"
                                    />
                                </Form.Item>

                                {
                                    !isEditingImages && images.length > 0 && (
                                        <AddedImages
                                            images={images}
                                            onEdit={() => setIsEditingImages(true)}
                                            Component={(
                                                <ImageUploadFormItem
                                                    images={images}
                                                    onImageChange={onImageChange}
                                                    variant="add-more"
                                                />
                                            )}
                                        />)
                                }
                            </>
                        )
                    }
                </Modal >
            </Form >
        </>
    )
}
